import React, { useCallback } from "react";
import { connect } from "react-redux";

import { useParams } from "react-router-dom";

import Colored from "@mapmycustomers/shared/types/base/Colored";
import Shaped from "@mapmycustomers/shared/types/base/Shaped";
import { Company, Deal, EntityType, Person } from "@mapmycustomers/shared/types/entity";
import { isDefined } from "@mapmycustomers/shared/util/assert";
import { LoadingSpinner } from "@mapmycustomers/ui";

import { CompanyRecordPane, DealRecordPane, PersonRecordPane } from "@app/component/preview";
import PinLocationToolButton from "@app/scene/map/components/PinLocationToolButton";
import BackButton from "@app/scene/map/components/sidebar/BackButton";
import {
  enterPinLocationMode,
  setChangesDiscarded,
  setPinLocationColor,
  setPinLocationShape,
} from "@app/scene/map/store/actions";
import { hideMapEntityView, setFocusedEntity } from "@app/scene/map/store/mapMode/actions";

import styles from "./EntityRecordView.module.scss";

interface Props {
  backButtonLabel?: React.ReactNode;
  hideMapEntityView: typeof hideMapEntityView;
  onChangeEntityColor: (color: Colored["color"]) => void;
  onChangeEntityShape: (color: Shaped["shape"]) => void;
  onClose: () => void;
  onDiscardChanges: typeof setChangesDiscarded;
  onEnterPinLocationMode: typeof enterPinLocationMode;
  setFocusedEntity: typeof setFocusedEntity;
}

const EntityRecordView: React.FC<Props> = ({
  backButtonLabel,
  hideMapEntityView,
  onChangeEntityColor,
  onChangeEntityShape,
  onDiscardChanges,
  onEnterPinLocationMode,
  setFocusedEntity,
}) => {
  const { sidebarEntityId, sidebarEntityType: entityType } = useParams<{
    sidebarEntityId: string | undefined;
    sidebarEntityType: EntityType | undefined;
  }>();

  const handleClose = useCallback(() => {
    hideMapEntityView();
    setFocusedEntity(undefined);
  }, [hideMapEntityView, setFocusedEntity]);

  const isVisible = isDefined(sidebarEntityId) && isDefined(entityType);

  if (!isVisible) {
    return null;
  }

  const entityId = parseInt(sidebarEntityId, 10);

  return (
    <div className={styles.container}>
      <BackButton className={styles.back} label={backButtonLabel} onClick={handleClose} />
      <div className={styles.wrapper}>
        {entityType === EntityType.COMPANY && (
          <React.Suspense fallback={<LoadingSpinner global />}>
            <CompanyRecordPane
              companyId={entityId as Company["id"]}
              isMapMode
              locationEditComponent={PinLocationToolButton}
              onChangeCompanyColor={onChangeEntityColor}
              onChangeCompanyShape={onChangeEntityShape}
              onDiscardChanges={onDiscardChanges}
              onEnterPinLocationMode={onEnterPinLocationMode}
              onHide={handleClose}
            />
          </React.Suspense>
        )}
        {entityType === EntityType.DEAL && (
          <React.Suspense fallback={<LoadingSpinner global />}>
            <DealRecordPane
              dealId={entityId as Deal["id"]}
              isMapMode
              onDiscardChanges={onDiscardChanges}
              onHide={handleClose}
            />
          </React.Suspense>
        )}
        {entityType === EntityType.PERSON && (
          <React.Suspense fallback={<LoadingSpinner global />}>
            <PersonRecordPane
              isMapMode
              locationEditComponent={PinLocationToolButton}
              onChangePersonColor={onChangeEntityColor}
              onChangePersonShape={onChangeEntityShape}
              onDiscardChanges={onDiscardChanges}
              onEnterPinLocationMode={onEnterPinLocationMode}
              onHide={handleClose}
              personId={entityId as Person["id"]}
            />
          </React.Suspense>
        )}
      </div>
    </div>
  );
};

const mapDispatchToProps = {
  hideMapEntityView,
  onChangeEntityColor: setPinLocationColor,
  onChangeEntityShape: setPinLocationShape,
  onDiscardChanges: setChangesDiscarded,
  onEnterPinLocationMode: enterPinLocationMode,
  setFocusedEntity,
};

export default connect(null, mapDispatchToProps)(EntityRecordView);
